import React from "react";
import { Trans, useTranslation } from "react-i18next";
import ComicButton from "../../shared/components/ComicButton/ComicButton";

const PrivacyPolicy = ({ onBack }) => {
  // Función para procesar saltos de línea en el texto
  const { t } = useTranslation();
  const renderWithLineBreaks = (key) => {
    const text = t(key);
    return { __html: text.replace(/\n/g, "<br>") };
  };

  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
      {/* Contenedor principal con ajustes responsivos */}
      <div className="flex flex-col items-center justify-center px-4 w-full h-full max-w-4xl overflow-hidden">
        {/* Título */}
        <h1
          className="text-yellow-500 font-bold mb-4"
          style={{
            fontSize: "clamp(1.5rem, 3vw, 2.5rem)", // Tamaño de fuente adaptativo
          }}
        >
          <Trans i18nKey="privacy.title">
            Política de Privacidad de Zaquiel Cómic
          </Trans>
        </h1>

        {/* Cuerpo del texto con scroll interno */}
        <div
          className="text-white text-left leading-relaxed"
          style={{
            fontSize: "clamp(0.8rem, 2vw, 1.2rem)", // Tamaño de fuente adaptativo
            maxHeight: "60%", // Limitar altura
            overflowY: "auto", // Habilitar scroll interno si es necesario
          }}
        >
          <p className="mb-4">
            <Trans i18nKey="privacy.lastUpdate">
              Última actualización: 26/12/2024
            </Trans>
          </p>
          <p className="mb-4">
            <Trans i18nKey="privacy.intro">
              En Zaquiel Cómic, nos comprometemos a proteger la privacidad de
              nuestros usuarios y a cumplir con el Reglamento General de
              Protección de Datos (RGPD). Esta política de privacidad detalla
              cómo recopilamos, usamos, almacenamos y protegemos tus datos
              personales. Al utilizar nuestro sitio web, aceptas los términos de
              esta política.
            </Trans>
          </p>
          <h2 className="text-yellow-500 font-bold mb-2">
            <Trans i18nKey="privacy.responsible.title">
              1. Responsable del tratamiento de los datos
            </Trans>
          </h2>
          {/* Texto con saltos de línea interpretados */}
          <p
            className="mb-4"
            dangerouslySetInnerHTML={renderWithLineBreaks(
              "privacy.responsible.description"
            )}
          ></p>

          <h2 className="text-yellow-500 font-bold mb-2">
            <Trans i18nKey="privacy.data.title">
              2. Datos personales recopilados
            </Trans>
          </h2>
          <p className="mb-4">
            <Trans i18nKey="privacy.data.description">
              Recopilamos los siguientes datos personales de forma anónima a
              través de nuestro formulario de opinión:
            </Trans>
          </p>
          <ul className="list-disc list-inside mb-4">
            <li>
              <Trans i18nKey="privacy.data.sex">Sexo</Trans>
            </li>
            <li>
              <Trans i18nKey="privacy.data.age">Edad</Trans>
            </li>
            <li>
              <Trans i18nKey="privacy.data.postalCode">
                Código postal (CP)
              </Trans>
            </li>
            <li>
              <Trans i18nKey="privacy.data.readingPreferences">
                Preferencias de lectura (digital o impreso)
              </Trans>
            </li>
            <li>
              <Trans i18nKey="privacy.data.habits">
                Hábitos de lectura y opiniones sobre el cómic
              </Trans>
            </li>
            <li>
              <Trans i18nKey="privacy.data.monumentResponses">
                Respuestas relacionadas con el monumento o lugar mencionado en
                el cómic
              </Trans>
            </li>
            <li>
              <Trans i18nKey="privacy.data.comments">
                Comentarios adicionales (opcional)
              </Trans>
            </li>
          </ul>
          <p className="mb-4">
            <Trans i18nKey="privacy.data.noIdentification">
              No recopilamos información que pueda identificarte directamente,
              como tu nombre, dirección de correo electrónico o dirección IP.
              Por este motivo, los datos almacenados no se pueden vincular a una
              persona específica.
            </Trans>
          </p>

          <h3 className="text-yellow-500 font-bold mb-2">
            <Trans i18nKey="privacy.dataNotifications.title">
              2.1. Suscripción a notificaciones por correo
            </Trans>
          </h3>
          <p className="mb-4">
            <Trans i18nKey="privacy.dataNotifications.description">
              Si eliges suscribirte a nuestras notificaciones por correo
              electrónico, recopilaremos y almacenaremos tu dirección de correo
              para informarte sobre nuevos capítulos y contenido. Estos datos se
              utilizan exclusivamente con fines de notificación y no se
              comparten con terceros.
            </Trans>
          </p>

          <h2 className="text-yellow-500 font-bold mb-2">
            <Trans i18nKey="privacy.purpose.title">
              3. Finalidad del tratamiento
            </Trans>
          </h2>
          <ul className="list-disc list-inside mb-4">
            <li>
              <Trans i18nKey="privacy.purpose.analyze">
                Analizar las opiniones de los lectores sobre el cómic y el
                lugar/monumento asociado.
              </Trans>
            </li>
            <li>
              <Trans i18nKey="privacy.purpose.improve">
                Mejorar nuestro contenido y experiencias ofrecidas.
              </Trans>
            </li>
            <li>
              <Trans i18nKey="privacy.purpose.statistics">
                Realizar estudios estadísticos de manera anónima.
              </Trans>
            </li>
          </ul>
          <h2 className="text-yellow-500 font-bold mb-2">
            <Trans i18nKey="privacy.legalBasis.title">
              4. Base legal para el tratamiento
            </Trans>
          </h2>
          <p className="mb-4">
            <Trans i18nKey="privacy.legalBasis.description">
              El tratamiento de tus datos se realiza con base en tu consentimiento
              expreso, otorgado al aceptar nuestra política de privacidad al
              completar el formulario. Puedes retirar tu consentimiento en
              cualquier momento (consulta la Sección 8).
            </Trans>
          </p>
          <h2 className="text-yellow-500 font-bold mb-2">
            <Trans i18nKey="privacy.storage.title">
              5. Período de almacenamiento
            </Trans>
          </h2>
          <p className="mb-4">
            <Trans i18nKey="privacy.storage.description">
              Tus datos personales serán almacenados durante el tiempo necesario
              para cumplir con las finalidades descritas anteriormente. Una vez
              cumplidas, los datos serán anonimizados o eliminados periódicamente.
            </Trans>
          </p>
          <h2 className="text-yellow-500 font-bold mb-2">
            <Trans i18nKey="privacy.recipients.title">
              6. Destinatarios de los datos
            </Trans>
          </h2>
          <ul className="list-disc list-inside mb-4">
            <li>
              <Trans i18nKey="privacy.recipients.legal">
                En cumplimiento de una obligación legal.
              </Trans>
            </li>
            <li>
              <Trans i18nKey="privacy.recipients.rights">
                Para proteger nuestros derechos legales o prevenir fraudes.
              </Trans>
            </li>
          </ul>
          <h2 className="text-yellow-500 font-bold mb-2">
            <Trans i18nKey="privacy.security.title">
              7. Seguridad de los datos
            </Trans>
          </h2>
          <ul className="list-disc list-inside mb-4">
            <li>
              <Trans i18nKey="privacy.security.https">
                Comunicaciones seguras mediante HTTPS.
              </Trans>
            </li>
            <li>
              <Trans i18nKey="privacy.security.access">
                Restricción de acceso a los datos a personal autorizado.
              </Trans>
            </li>
            <li>
              <Trans i18nKey="privacy.security.deletion">
                Eliminación periódica de datos antiguos.
              </Trans>
            </li>
          </ul>
          <h2 className="text-yellow-500 font-bold mb-2">
            <Trans i18nKey="privacy.rights.title">8. Derechos del usuario</Trans>
          </h2>
          <p className="mb-4">
            <Trans i18nKey="privacy.rights.description">
              Tienes derecho a retirar tu consentimiento y a realizar consultas
              sobre el tratamiento de tus datos. Dado que los datos son anónimos,
              no es posible vincularlos a un individuo para eliminarlos.
            </Trans>
          </p>
          <h2 className="text-yellow-500 font-bold mb-2">
            <Trans i18nKey="privacy.changes.title">
              9. Cambios en la política de privacidad
            </Trans>
          </h2>
          <p className="mb-4">
            <Trans i18nKey="privacy.changes.description">
              Nos reservamos el derecho de actualizar esta política de privacidad
              en cualquier momento. Te notificaremos sobre cambios significativos
              mediante un aviso en nuestra página web.
            </Trans>
          </p>
          <h2 className="text-yellow-500 font-bold mb-2">
            <Trans i18nKey="privacy.complaints.title">
              10. Reclamos ante la autoridad competente
            </Trans>
          </h2>
          <p className="mb-4">
            <Trans i18nKey="privacy.complaints.description">
              Si consideras que el tratamiento de tus datos no cumple con la
              normativa, tienes derecho a presentar un reclamo ante la Agencia
              Española de Protección de Datos (AEPD) o la autoridad correspondiente
              en tu país.
            </Trans>
          </p>
          <p>
            <Trans i18nKey="privacy.complaints.link">
              Enlace a la AEPD: https://www.aepd.es
            </Trans>
          </p>
        </div>

        {/* Botón para volver */}
        <div className="mt-6">
          <ComicButton
            buttonName={<Trans i18nKey="backToMenu">Volver al Menú</Trans>}
            hoverBtnName={<Trans i18nKey="backToMenu">Volver al Menú</Trans>}
            handleButtonClick={onBack}
          />
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
